import { frontendVersion } from 'helpers/versionRoutines'
import { useEffect } from 'react'
import { setConfig, setInstallVersion, setLogPath } from 'actions/app'
import { setOnnxNames } from 'actions/nabletHld'
import { setTranscribeModels } from 'actions/transcribe'
import { setAdobeCepExtentions } from 'actions/adobeCepExtentions'
import { useAction } from '~/hooks'
import { getVersion, checkToken } from '~/ServerAPI'
import versionInfo from './version.json'
import * as AuthToken from '~/cookieServices/AuthToken'
import { NW_JS_APP_PROFILES } from '~/enums'

function formatConsoleVersion(data) {
  const { hash, date: isoDateStr } = data

  return `${frontendVersion()} | ${hash} | ${isoDateStr}`
}

const checkUrlAuthToken = async ({ externalLoginUrl }) => {
  const params = new URLSearchParams(window.location.search)
  let authToken = params.get('auth_token') || params.get('session_id') || params.get('token')
  if (!authToken) {
    authToken = AuthToken.get()
  }
  const atSmartediting = window.location.host === __CFG__.SMARTEDITING_HOST
  if (externalLoginUrl && !atSmartediting && __APP_PROFILE__ !== 'package') {
    // in case of externalLoginUrl redirect to login page if no token specified or token expired
    if (authToken) {
      try {
        await checkToken(authToken)
      } catch (e) {
        authToken = null
      }
    }
    if (!authToken) {
      window.location.href = externalLoginUrl
    }
  }
  if (!authToken && __CFG__.GENERATE_AUTH_TOKEN) {
    authToken = Date.now().toString()
  }
  if (authToken) {
    AuthToken.set(authToken)
    window.userId = authToken
  }
}

async function waitForLocalStorage(timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    const start = Date.now()

    const check = () => {
      const done = localStorage.getItem('nwRestoreComplete')

      if (done) {
        resolve(true)
        return
      }

      if (Date.now() - start > timeoutMs) {
        const error = new Error('Timeout waiting for nwRestoreComplete in localStorage')
        reject(error)
        return
      }

      setTimeout(check, 100)
    }

    check()
  })
}

export default function AppVersion({ onSuccess }) {
  const setInstallVersionAction = useAction(setInstallVersion)
  const setOnnxNamesAction = useAction(setOnnxNames)
  const setTranscribeModelsAction = useAction(setTranscribeModels)
  const setAdobeCepExtentionsAction = useAction(setAdobeCepExtentions)
  const onSetLogPath = useAction(setLogPath)
  const setConfigAction = useAction(setConfig)
  useEffect(() => {
    async function showVersion() {
      try {
        if (NW_JS_APP_PROFILES.includes(__APP_PROFILE__)) {
          await waitForLocalStorage()
        }
        const response = await getVersion()
        if (response && response.data) {
          const { installVer, version,
            nbOnnxNames, transcribeModels,
            adobeCEPExts, config } = response.data
          if (installVer) {
            // eslint-disable-next-line no-console
            console.log(`Installation version ${installVer}`)
          }
          // eslint-disable-next-line no-console
          console.log(`Backend version ${version}`)
          // eslint-disable-next-line no-console
          console.log(`Frontend version ${formatConsoleVersion(versionInfo)}`)
          await checkUrlAuthToken(response.data)
          setInstallVersionAction(response.data)
          if (nbOnnxNames) {
            setOnnxNamesAction(nbOnnxNames)
          }
          if (transcribeModels) {
            setTranscribeModelsAction(transcribeModels)
          }
          if (adobeCEPExts && adobeCEPExts.length) {
            setAdobeCepExtentionsAction(adobeCEPExts)
          }
          if (config) {
            setConfigAction(config)
          }
          if (NW_JS_APP_PROFILES.includes(__APP_PROFILE__)) {
            onSetLogPath(response.data)
          }
          onSuccess()
        }
      } catch (e) {
        console.error('Failed to fetch backend version\n', e)
      }
    }
    showVersion()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])// Intentionally left dependencies array empty to run only once

  return null
}
